import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react'
import {
    getReport, reportPdfUrl, invoicePdfUrl, invoicePdfTabId, importStripe, importMollie, importPaypal, sendZip,
    getDocumentsIndex, listMonthDocuments, ensureMonthDocuments, uploadDocuments, deleteDocument, moveDocument, setDocumentAmount, setDocumentExpenseType, documentFileUrl,
    deleteMonthDirectory, deleteYearDirectory,
    getChecklist, getChecklistBindings, addChecklistItem, setChecklistItem, updateChecklistItem, deleteChecklistItem, setMonthClosed,
    pollIngest, getIngestItems, ingestItemAction,
    getExpenseTypes,
    type MonthlyReport, type MonthListing, type ChecklistView, type ChecklistBinding, type ChecklistItem, type ChecklistRequirement, type IngestionItem, type ExpenseType,
} from '../api'
import InfoTip from '../components/InfoTip'
import ConfirmDialog from '../components/ConfirmDialog'
import Drawer from '../components/Drawer'
import { useToast } from '../layout/ToastContext'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { MONTH_ROOT_FOLDER, formatMoney, moneySign, docCategoryLabel } from '../lookups'
import { fill, CHECK_MARKS, CHECK_MARK_DETAIL } from '../../shared/texts'
import { checkWithin } from '../forms/checkForm'
import { CHECK_MARK_SYMBOLS, INGEST_FROM_MAX_CHARS, INGEST_SUBJECT_MAX_CHARS } from '../../shared/constants'
import { PAGES_MONTHLY_EXPORT_TEXTS as T } from '../../shared/texts'

// The file explorer defaults to the CURRENT month when the export tab is opened
// (see selYear/selMonth below); the selection is then kept until the tab closes.
const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
const monthName = (m: number) => new Date(2000, m - 1, 1).toLocaleDateString('de-DE', { month: 'long' })

// The one formatter (constants.ts): the record's currency, else the backoffice currency.
const money = (n: number, currency?: string) => formatMoney(n, currency)
function fileSize(n: number): string {
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
function shortDate(iso?: string): string {
    return iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'
}
const INGEST_REASON: Record<string, string> = T.ingestReason
const INGEST_STATUS: Record<string, { label: string, cls: string }> = {
    imported: { label: T.ingestStatus.imported, cls: 'sent' },
    problem: { label: T.ingestStatus.problem, cls: 'draft' },
    ignored: { label: T.ingestStatus.ignored, cls: '' },
}
type CheckMark = keyof typeof CHECK_MARK_SYMBOLS
// One symbol of a checklist row; `detail` (brands, binding) goes into the tooltip only.
function Mark({ mark, detail }: { mark: CheckMark, detail?: string }) {
    const { sym, cls } = CHECK_MARK_SYMBOLS[mark]
    const text = detail ? fill(CHECK_MARK_DETAIL, { text: CHECK_MARKS[mark], detail }) : CHECK_MARKS[mark]
    return <span className={`check-mark ${cls}`} title={text} aria-label={text} role="img">{sym}</span>
}

// Jahr/Monat-Picker (reuses the .modal styling). Used to confirm the upload target
// and to pick the target month when moving a file to another year. The category
// stays fixed (shown as a label); only year + month are chosen.
function PeriodDialog({ title, confirmLabel, typeLabel, count, initYear, initMonth, years, busy, error, onConfirm, onCancel }: {
    title: string, confirmLabel: string, typeLabel: string, count?: number,
    initYear: number, initMonth: number, years: number[], busy: boolean, error: string,
    onConfirm: (year: number, month: number) => void, onCancel: () => void,
}) {
    const [y, setY] = useState(initYear)
    const [m, setM] = useState(initMonth)
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [busy, onCancel])
    return (
        <div className="modal-backdrop" onMouseDown={() => !busy && onCancel()}>
            <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
                <h2>{title}</h2>
                <div className="field"><label>{T.type}</label><div><strong>{typeLabel}</strong></div></div>
                <div className="row">
                    <div className="field">
                        <label>{T.year}</label>
                        <select value={y} disabled={busy} onChange={(e) => setY(Number(e.target.value))}>
                            {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>{T.month}</label>
                        <select value={m} disabled={busy} onChange={(e) => setM(Number(e.target.value))}>
                            {MONTHS.map((_, i) => <option key={i + 1} value={i + 1}>{monthName(i + 1)}</option>)}
                        </select>
                    </div>
                </div>
                {count != null && <p className="muted">{fill(T.fileCount, { n: count })}</p>}
                {error && <div className="notice err">{error}</div>}
                <div className="modal-actions">
                    <button type="button" onClick={() => onConfirm(y, m)} disabled={busy}>{busy ? '…' : confirmLabel}</button>
                    <button type="button" className="secondary" onClick={onCancel} disabled={busy}>{T.cancel}</button>
                </div>
            </div>
        </div>
    )
}

export default function MonthlyExport() {
    const { toast } = useToast()
    const { openTab, publishChange } = useTabs()
    const { can } = useAuth()
    // Live-refresh of the month folders ('documents': a mail-PDF scan filed a document, an
    // invoice or credit note PDF was stored, another client changed the folder) and of the
    // inbox ('ingest'). The report is computed from the invoices, so it follows 'invoices'.
    // The folders deliberately do NOT follow 'invoices': the server announces an invoice
    // before its PDF exists, so a reload then would look too early.
    const docsVersion = useDataVersion('documents')
    const ingestVersion = useDataVersion('ingest')
    const invoicesVersion = useDataVersion('invoices')
    const canWrite = can('export', 'write')
    const canDelete = can('export', 'delete')

    // Pill switcher: 'export' = Ablage/Checkliste/Aktionen · 'inbox' = E-Mail-Beleg-Eingang
    const [view, setView] = useState<'export' | 'inbox'>('export')

    const [index, setIndex] = useState<Record<string, number[]>>({})
    const [selYear, setSelYear] = useState<number>(() => new Date().getFullYear())
    const [selMonth, setSelMonth] = useState<number | null>(() => new Date().getMonth() + 1)
    const [selCategory, setSelCategory] = useState<string | null>(null)

    const [listing, setListing] = useState<MonthListing | null>(null)
    const [checklist, setChecklist] = useState<ChecklistView | null>(null)
    const [bindings, setBindings] = useState<ChecklistBinding[]>([])
    const [report, setReport] = useState<MonthlyReport | null>(null)

    // Several actions may run at once — they hit different APIs and write different files.
    // A single "busy" string would force them into a queue for no reason, which is why every
    // button used to grey out as soon as one of them ran.
    const [running, setRunning] = useState<string[]>([])
    const isBusy = (name: string) => running.includes(name)
    const anyBusy = running.length > 0
    // The send builds the ZIP from the whole month folder, so it must not overlap with an import.
    const zipRunning = isBusy('send')
    const [error, setError] = useState('')
    const [dragCat, setDragCat] = useState<string | null>(null)
    // Upload now goes through a confirm dialog (avoids dropping into the wrong month);
    // files are dragged between months via dragFile + the month/year columns as targets.
    const [pendingUpload, setPendingUpload] = useState<{ files: File[], folder: string } | null>(null)
    const [pendingMove, setPendingMove] = useState<{ folder: string, name: string, toYear: number } | null>(null)
    const [dragFile, setDragFile] = useState<{ folder: string, name: string } | null>(null)
    const [dragOver, setDragOver] = useState<string | null>(null) // "month:6" | "year:2025" highlight
    // Per-file ⋮ menu (rendered fixed so the scrolling file list can't clip it) +
    // the "assign expense type" dialog it opens.
    const [fileMenu, setFileMenu] = useState<{ name: string, x: number, y: number } | null>(null)
    const [expenseFor, setExpenseFor] = useState<{ name: string } | null>(null)
    const [expenseSel, setExpenseSel] = useState('')
    const [toDelete, setToDelete] = useState<{ folder: string, name: string } | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    // Rechtsklick-Kontextmenü auf Jahr/Monat + Lösch-Bestätigung (nur leere löschbar).
    const [dirMenu, setDirMenu] = useState<{ kind: 'year' | 'month', value: number, x: number, y: number } | null>(null)
    const [toDeleteDir, setToDeleteDir] = useState<{ kind: 'year' | 'month', value: number } | null>(null)
    const [dirDelBusy, setDirDelBusy] = useState(false)
    const [newLabel, setNewLabel] = useState('')
    const [newBinding, setNewBinding] = useState('')
    const [editId, setEditId] = useState<string | null>(null)
    const [editLabel, setEditLabel] = useState('')
    const [editBinding, setEditBinding] = useState('')
    const [editRequirement, setEditRequirement] = useState<ChecklistRequirement>('required')
    const [editMailNote, setEditMailNote] = useState(false)
    const [ingest, setIngest] = useState<IngestionItem[]>([])
    const [ingestBusy, setIngestBusy] = useState<string | null>(null)
    const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([])
    const typeLabel = useMemo(() => {
        const m = new Map(expenseTypes.map((t) => [t.key, t.label]))
        return (key?: string) => (key ? m.get(key) ?? key : '')
    }, [expenseTypes])

    const reloadIndex = useCallback(() => getDocumentsIndex().then(setIndex).catch(() => {}), [])
    const reloadIngest = useCallback(() => getIngestItems().then(setIngest).catch(() => {}), [])
    useEffect(() => { reloadIndex() }, [reloadIndex, docsVersion])
    useEffect(() => { reloadIngest() }, [reloadIngest, ingestVersion])
    useEffect(() => { getChecklistBindings().then(setBindings).catch(() => {}) }, [])
    useEffect(() => { getExpenseTypes().then(setExpenseTypes).catch(() => {}) }, [])

    // Load the selected month's folders + checklist whenever the period changes.
    const loadMonth = useCallback(async () => {
        setError('')
        if (selMonth == null) { setListing(null); setChecklist(null); return }
        const [l, c] = await Promise.all([listMonthDocuments(selYear, selMonth), getChecklist(selYear, selMonth)])
        setListing(l); setChecklist(c)
    }, [selYear, selMonth])
    useEffect(() => { loadMonth().catch((e) => setError(e?.response?.data?.error ?? e?.message ?? T.loadError)) }, [loadMonth, docsVersion])
    // The report belongs to its month: gone when the month changes, but a new file in the folder
    // no longer throws it away. An invoice change re-reads a report that is on screen.
    useEffect(() => { setReport(null) }, [selYear, selMonth])
    const reportShown = report != null
    useEffect(() => {
        if (reportShown && selMonth != null) getReport(selYear, selMonth).then(setReport).catch(() => {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicesVersion])

    async function run(name: string, fn: () => Promise<void>) {
        setRunning((r) => [...r, name]); setError('')
        // Every action except showing the report writes to the month (files, reports, checklist,
        // ZIP) — tell the other open export views.
        try { await fn(); if (name !== 'report') publishChange('documents') }
        catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? T.error) }
        finally { setRunning((r) => r.filter((n) => n !== name)) }
    }
    const refreshChecklist = () => selMonth != null && getChecklist(selYear, selMonth).then(setChecklist).catch(() => {})

    // --- Column navigation ---
    const years = useMemo(() => {
        const cur = new Date().getFullYear()
        const set = new Set<number>([cur, cur + 1, ...Object.keys(index).map(Number)])
        return [...set].sort((a, b) => b - a)
    }, [index])
    const existingMonths = index[String(selYear)] ?? []

    const pickYear = (y: number) => { setSelYear(y); setSelMonth(null); setSelCategory(null); setListing(null); setChecklist(null) }
    const pickMonth = (m: number) => { setSelMonth(m); setSelCategory(null) }
    const pickCategory = (c: string) => setSelCategory(c)

    const createMonth = () => run('create', async () => {
        if (selMonth == null) return
        setListing(await ensureMonthDocuments(selYear, selMonth))
        await reloadIndex()
    })

    // Picking/dropping files no longer uploads immediately — it opens a confirm
    // dialog so the target month/year can be checked first.
    const requestUpload = (folder: string, files: FileList | File[]) => {
        const arr = Array.from(files)
        if (arr.length) setPendingUpload({ files: arr, folder })
    }
    const doUpload = (y: number, m: number) => {
        if (!pendingUpload) return
        const { files, folder } = pendingUpload
        run('upload', async () => {
            const res = await uploadDocuments(y, m, folder, files)
            setPendingUpload(null)
            // Jump to the target so the user sees where the files landed (loadMonth
            // refetches the listing + checklist via the selYear/selMonth effect).
            setSelYear(y); setSelMonth(m); setSelCategory(folder)
            await reloadIndex()
            toast(fill(T.uploaded, { n: res.uploaded.length, month: monthName(m), year: y }))
        })
    }
    // External file drop onto the files column → same confirm dialog.
    const onDrop = (folder: string) => (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault(); setDragCat(null)
        if (e.dataTransfer?.files?.length) requestUpload(folder, e.dataTransfer.files)
    }

    // --- Move a file between months (drag a file onto a month/year column) ---
    // The name a file is SHOWN under (an invoice PDF: its outgoing name) — for dialogs and toasts; the
    // stored `name` stays what every request sends.
    const shownName = (folder: string, name: string) =>
        listing?.folders.find((f) => f.name === folder)?.files.find((x) => x.name === name)?.displayName ?? name
    const doMoveDirect = (toMonth: number) => {
        if (!dragFile || selMonth == null) return
        const { folder, name } = dragFile
        setDragFile(null); setDragOver(null)
        if (toMonth === selMonth) return // same month → nothing to do
        run('move', async () => {
            const l = await moveDocument(selYear, selMonth!, folder, name, selYear, toMonth)
            setListing(l); await refreshChecklist(); await reloadIndex()
            toast(fill(T.movedTo, { name: shownName(folder, name), target: monthName(toMonth) }))
        })
    }
    // Drop a file onto another type (same month) → move between categories.
    const doMoveToFolder = (toFolder: string) => {
        if (!dragFile || selMonth == null) return
        const { folder, name } = dragFile
        setDragFile(null); setDragOver(null)
        if (toFolder === folder) return // same type → nothing to do
        run('move', async () => {
            const l = await moveDocument(selYear, selMonth!, folder, name, selYear, selMonth!, toFolder)
            setListing(l); await refreshChecklist()
            toast(fill(T.movedTo, { name: shownName(folder, name), target: docCategoryLabel(toFolder) }))
        })
    }
    const doMove = (y: number, m: number) => {
        if (!pendingMove || selMonth == null) return
        const { folder, name } = pendingMove
        if (y === selYear && m === selMonth) { setPendingMove(null); return }
        run('move', async () => {
            const l = await moveDocument(selYear, selMonth!, folder, name, y, m)
            setPendingMove(null)
            setListing(l); await refreshChecklist(); await reloadIndex()
            toast(fill(T.movedTo, { name: shownName(folder, name), target: `${monthName(m)} ${y}` }))
        })
    }

    // Inline-edit the stored amount of a vendor invoice (drives the in/out balance).
    const saveAmount = (folder: string, name: string, raw: string) => {
        if (selMonth == null) return
        const t = raw.trim().replace(/\./g, '').replace(',', '.') // accept "1.234,56"
        const val = t === '' ? null : Number(t)
        if (val !== null && !Number.isFinite(val)) { setError(T.invalidAmount); return }
        setDocumentAmount(selYear, selMonth, folder, name, val)
            .then((l) => { setListing(l); publishChange('documents') })
            .catch((e: any) => setError(e?.response?.data?.error ?? T.amountSaveFailed))
    }

    // Close the ⋮ file menu on Escape / any scroll (it is fixed-positioned).
    useEffect(() => {
        if (!fileMenu) return
        const close = () => setFileMenu(null)
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', close, true)
        return () => { document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', close, true) }
    }, [fileMenu])

    // Same close-on-Escape/scroll for the year/month context menu (fixed-positioned).
    useEffect(() => {
        if (!dirMenu) return
        const close = () => setDirMenu(null)
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', close, true)
        return () => { document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', close, true) }
    }, [dirMenu])

    // Is a month known to be empty? For the currently loaded month we know from the
    // listing; for others it's unknown (null) and the server decides on delete.
    const monthEmptyKnown = (m: number): boolean | null => {
        if (selMonth === m && listing) return !(listing.rootFiles.length || listing.folders.some((f) => f.files.length))
        return null
    }

    // Delete an empty month/year folder (server enforces emptiness → clear error on 409).
    async function confirmDeleteDir() {
        if (!toDeleteDir) return
        const { kind, value } = toDeleteDir
        setDirDelBusy(true); setError('')
        try {
            const idx = kind === 'year' ? await deleteYearDirectory(value) : await deleteMonthDirectory(selYear, value)
            setIndex(idx); publishChange('documents')
            // Clear the selection if the removed node was selected.
            if (kind === 'year' && selYear === value) { setSelMonth(null); setSelCategory(null); setListing(null); setChecklist(null) }
            if (kind === 'month' && selMonth === value) { setSelMonth(null); setSelCategory(null); setListing(null); setChecklist(null) }
            setToDeleteDir(null)
            toast(fill(T.deletedToast, { label: kind === 'year' ? fill(T.yearLabel, { year: value }) : `${monthName(value)} ${selYear}` }))
        } catch (e: any) {
            setError(e?.response?.data?.error ?? T.deleteFailedDot)
        } finally { setDirDelBusy(false) }
    }

    // Manually assign / clear a vendor invoice's expense type (one-off invoices,
    // or correcting a wrong auto-classification).
    const openExpenseDialog = (name: string, current?: string) => { setExpenseSel(current ?? ''); setExpenseFor({ name }) }
    const saveExpenseType = () => {
        if (!expenseFor || selMonth == null || !selCategory) return
        run('expense', async () => {
            const l = await setDocumentExpenseType(selYear, selMonth!, selCategory, expenseFor.name, expenseSel || null)
            setListing(l); setExpenseFor(null)
            toast(T.expenseTypeSaved)
        })
    }

    async function confirmDelete() {
        if (!toDelete || selMonth == null) return
        setDelBusy(true)
        try {
            const l = await deleteDocument(selYear, selMonth, toDelete.folder, toDelete.name)
            publishChange('documents')
            setListing(l); await refreshChecklist(); setToDelete(null)
            toast(T.fileDeleted)
        } catch (e: any) {
            setError(e?.response?.data?.error ?? T.deleteFailed)
        } finally { setDelBusy(false) }
    }

    // --- Checklist ---
    const toggleItem = (id: string, manualDone: boolean) =>
        selMonth != null && setChecklistItem(selYear, selMonth, id, !manualDone).then((c) => { setChecklist(c); publishChange('documents') }).catch(() => {})
    const addItem = (e?: { currentTarget: EventTarget | null }) => {
        if (!checkWithin(e, '.check-add')) return
        const label = newLabel.trim()
        if (!label || selMonth == null) return
        run('additem', async () => {
            setChecklist(await addChecklistItem(selYear, selMonth!, { label, binding: newBinding || undefined }))
            setNewLabel(''); setNewBinding('')
        })
    }
    const removeItem = (id: string) => selMonth != null && deleteChecklistItem(selYear, selMonth, id).then(setChecklist).catch(() => {})
    const startEdit = (it: ChecklistItem) => {
        setEditId(it.id); setEditLabel(it.label); setEditBinding(it.binding ?? '')
        setEditRequirement(it.requirement); setEditMailNote(it.mailNote)
    }
    // „Kann leer sein“ and the mail note only exist behind a payment-report binding.
    const editCanBeEmpty = !!bindings.find((b) => b.key === editBinding)?.canBeEmpty
    const cancelEdit = () => { setEditId(null); setEditLabel(''); setEditBinding('') }
    // A binding is 1:1 — true if another item (≠ exceptId) already uses it.
    const bindingUsed = (key: string, exceptId?: string) =>
        !!checklist?.items.some((i) => i.binding === key && i.id !== exceptId)
    const saveEdit = (id: string, e?: { currentTarget: EventTarget | null }) => {
        if (!checkWithin(e, '.check-edit')) return
        const label = editLabel.trim()
        if (!label || selMonth == null) return
        run('edititem', async () => {
            const requirement = editRequirement === 'may-be-empty' && !editCanBeEmpty ? 'required' : editRequirement
            setChecklist(await updateChecklistItem(selYear, selMonth!, id, {
                label, binding: editBinding, requirement, mailNote: editCanBeEmpty && editMailNote,
            }))
            cancelEdit()
        })
    }

    // --- Actions ---
    const loadReport = () => run('report', async () => { if (selMonth != null) setReport(await getReport(selYear, selMonth)) })
    // One workbook per brand, with the scheduled Stripe reports as extra sheets.
    const doStripe = () => run('stripe', async () => {
        if (selMonth == null) return
        const r = await importStripe(selYear, selMonth)
        const note = r.skipped?.length ? fill(T.skippedNote, { list: r.skipped.join(', ') }) : ''
        toast(fill(T.stripeDone, { files: r.paths.length, count: r.count, note }))
        await loadMonth(); await reloadIndex()
    })
    const doPaypal = () => run('paypal', async () => {
        if (selMonth == null) return
        const r = await importPaypal(selYear, selMonth)
        toast(r.path ? fill(T.paypalDone, { count: r.count }) : T.paypalNone)
        await loadMonth(); await reloadIndex()
    })
    const doMollie = () => run('mollie', async () => {
        if (selMonth == null) return
        const r = await importMollie(selYear, selMonth)
        toast(r.path ? fill(T.mollieDone, { count: r.count }) : T.mollieNone)
        await loadMonth(); await reloadIndex()
    })
    const doSend = () => run('send', async () => {
        if (selMonth == null) return
        const r: any = await sendZip(selYear, selMonth)
        toast(r?.sent === false ? T.smtpMissing : T.zipSent)
        // A successful send closes the month server-side — reflect that right away; the folder
        // now holds the freshly built report under payment-reports/.
        refreshChecklist()
        await loadMonth(); await reloadIndex()
    })
    // Close / re-open by hand: months handed over before this existed need it set
    // once, and a month is re-opened when a correction still has to go out.
    const toggleClosed = (closed: boolean) => run('closed', async () => {
        if (selMonth == null) return
        setChecklist(await setMonthClosed(selYear, selMonth, closed))
        toast(closed
            ? T.monthClosedToast
            : T.monthReopenedToast)
    })
    const doPoll = () => run('poll', async () => {
        const r = await pollIngest()
        if (r.busy) { toast(T.pollRunning); return }
        toast(fill(T.polled, { imported: r.imported, problems: r.problems, skipped: r.skipped }))
        await reloadIngest(); await reloadIndex(); if (selMonth != null) await loadMonth()
    })

    // Problem-list actions: retry (re-classify), accept (file anyway), ignore.
    const doIngestAction = (id: string, action: 'retry' | 'accept' | 'ignore') => {
        setIngestBusy(`${id}:${action}`); setError('')
        ingestItemAction(id, action)
            .then((r) => toast(
                action === 'ignore' ? T.ingestIgnored
                    : r.gone ? T.ingestGone
                        : r.ok ? T.ingestFiled : T.ingestStillNoInvoice))
            .catch((e: any) => setError(e?.response?.data?.error ?? T.actionFailed))
            .finally(async () => { setIngestBusy(null); await reloadIngest(); await reloadIndex(); if (selMonth != null) await loadMonth() })
    }

    // Open a file / the report in an in-app viewer tab (inline, not a download).
    // `name` is the stored file (the key), `shown` what the tab says — an invoice PDF's outgoing name.
    // An invoice's PDF opens in the invoice's own tab — the same id and address as from the list and the form, so
    // the same invoice never sits in two tabs (operator, 01.11.2026). Other files keep their file tab.
    const openFile = (folder: string, name: string, shown = name, invoiceId?: string) => {
        if (selMonth == null) return
        if (invoiceId) {
            openTab({ id: invoicePdfTabId(invoiceId), name: shown, component: 'file-viewer', config: { url: invoicePdfUrl(invoiceId, shown), fname: shown } })
            return
        }
        openTab({
            id: `file:${selYear}-${selMonth}-${folder}-${name}`,
            name: shown,
            component: 'file-viewer',
            config: { url: documentFileUrl(selYear, selMonth, folder, name, shown), fname: shown },
        })
    }
    const openReport = async () => {
        if (selMonth == null) return
        // The file name comes with the report data; fetched here if the report is not on screen yet.
        const pdfName = (report && report.year === selYear && report.month === selMonth ? report : await getReport(selYear, selMonth)).pdfName
        openTab({
            id: `report:${selYear}-${selMonth}`,
            name: fill(T.reportTitle, { month: String(selMonth).padStart(2, '0'), year: selYear }),
            component: 'file-viewer',
            config: { url: reportPdfUrl(selYear, selMonth, pdfName), fname: pdfName },
        })
    }

    const folders = listing?.folders ?? []
    // The month folder itself: only open and delete — no upload, no move, no amount.
    const isRoot = selCategory === MONTH_ROOT_FOLDER
    const rootFiles = listing?.rootFiles ?? []
    const files = isRoot ? rootFiles : selCategory ? (folders.find((f) => f.name === selCategory)?.files ?? []) : []
    const openItems = checklist ? checklist.items.filter((i) => i.required && !i.done).map((i) => i.label) : []
    const sendDisabled = anyBusy || !checklist?.allDone
    const monthClosed = !!checklist?.closedAt
    // One short line, no explanation: a recorded send outranks a hand-set close, since
    // it is the stronger fact. What a close *means* goes to the log, not here.
    const deDate = (iso: string) => new Date(iso).toLocaleDateString('de-DE')
    const statusText = checklist?.sentAt
        ? fill(T.sentOn, { date: deDate(checklist.sentAt) })
        : (checklist?.closedAt ? fill(T.closedOn, { date: deDate(checklist.closedAt) }) : '')
    const ingestProblems = ingest.filter((i) => i.status === 'problem').length

    return (
        <div className="export-page">
            {/* Fixer Kopf: Titel + Fehlermeldung + Pills bleiben sichtbar, Body scrollt. */}
            <div className="export-top">
                <div className="row" style={{ marginBottom: 18 }}>
                    <div className="page-title">
                        <h1>{T.title}</h1>
                        <InfoTip>{T.info}</InfoTip>
                    </div>
                    {canWrite && (
                        <button type="button" className="secondary" disabled={anyBusy} onClick={doPoll}
                            title={T.pollTitle}>
                            {isBusy('poll') ? '…' : T.poll}
                        </button>
                    )}
                </div>

                {error && <div className="notice err">{error}</div>}

                <div className="pill-tabs" role="tablist" style={{ marginBottom: 0 }}>
                    <button type="button" role="tab" className={`pill-tab${view === 'export' ? ' active' : ''}`}
                        aria-selected={view === 'export'} onClick={() => setView('export')}>
                        {T.tabExport}
                    </button>
                    <button type="button" role="tab" className={`pill-tab${view === 'inbox' ? ' active' : ''}`}
                        aria-selected={view === 'inbox'} onClick={() => setView('inbox')}>
                        {T.tabInbox}{ingestProblems ? ` (${ingestProblems})` : ''}
                    </button>
                </div>
            </div>

            <div className="export-body">
            {view === 'export' && (<>
            {/* Datei-Explorer (links) + Aktionen (rechts) nebeneinander */}
            <div className="export-split">
              <div className="export-main">
            {/* Miller-columns file browser */}
            <div className="panel">
                <div className="miller">
                    {/* Column 1: Year */}
                    <div className="miller-col col-year">
                        <div className="miller-head">{T.year}</div>
                        <div className="miller-list">
                            {years.map((y) => (
                                <button key={y} type="button"
                                    className={`miller-item${y === selYear ? ' active' : ''}`}
                                    style={dragFile && dragOver === `year:${y}` ? { outline: '2px dashed currentColor', outlineOffset: '-2px' } : undefined}
                                    onClick={() => pickYear(y)}
                                    onContextMenu={canDelete && index[String(y)] !== undefined ? (e) => { e.preventDefault(); setDirMenu({ kind: 'year', value: y, x: e.clientX, y: e.clientY }) } : undefined}
                                    onDragOver={dragFile ? (e) => { e.preventDefault(); setDragOver(`year:${y}`) } : undefined}
                                    onDragLeave={() => setDragOver(null)}
                                    onDrop={dragFile ? (e) => { e.preventDefault(); setPendingMove({ folder: dragFile.folder, name: dragFile.name, toYear: y }); setDragFile(null); setDragOver(null) } : undefined}>
                                    <span title={String(y)}>{y}</span>
                                    {index[String(y)]?.length ? <span className="miller-dot" /> : <span className="muted miller-tag">{T.tagNew}</span>}
                                    <span className="miller-chev">›</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Column 2: Month */}
                    <div className="miller-col col-month">
                        <div className="miller-head">{T.month}</div>
                        <div className="miller-list">
                            {MONTHS.map((mm, i) => {
                                const m = i + 1
                                const has = existingMonths.includes(m)
                                return (
                                    <button key={mm} type="button"
                                        className={`miller-item${m === selMonth ? ' active' : ''}`}
                                        style={dragFile && dragOver === `month:${m}` && m !== selMonth ? { outline: '2px dashed currentColor', outlineOffset: '-2px' } : undefined}
                                        onClick={() => pickMonth(m)}
                                        onContextMenu={canDelete && has ? (e) => { e.preventDefault(); setDirMenu({ kind: 'month', value: m, x: e.clientX, y: e.clientY }) } : undefined}
                                        onDragOver={dragFile && m !== selMonth ? (e) => { e.preventDefault(); setDragOver(`month:${m}`) } : undefined}
                                        onDragLeave={() => setDragOver(null)}
                                        onDrop={dragFile && m !== selMonth ? (e) => { e.preventDefault(); doMoveDirect(m) } : undefined}>
                                        <span className={has ? '' : 'muted'} title={mm}>{mm}</span>
                                        {has ? <span className="miller-dot" /> : <span className="muted miller-tag">{T.tagNew}</span>}
                                        <span className="miller-chev">›</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Column 3: Category */}
                    <div className="miller-col col-cat">
                        <div className="miller-head">
                            {T.type}
                            {selMonth != null && listing && !listing.exists && canWrite && (
                                <button className="link-btn" disabled={anyBusy} onClick={createMonth}>
                                    {isBusy('create') ? '…' : T.createMonth}
                                </button>
                            )}
                        </div>
                        <div className="miller-list">
                            {selMonth == null ? <p className="muted miller-empty">{T.pickMonth}</p> : folders.map((f) => (
                                <button key={f.name} type="button"
                                    className={`miller-item${f.name === selCategory ? ' active' : ''}`}
                                    style={dragFile && dragOver === `cat:${f.name}` && f.name !== dragFile.folder ? { outline: '2px dashed currentColor', outlineOffset: '-2px' } : undefined}
                                    onClick={() => pickCategory(f.name)}
                                    onDragOver={dragFile && f.name !== dragFile.folder ? (e) => { e.preventDefault(); setDragOver(`cat:${f.name}`) } : undefined}
                                    onDragLeave={() => setDragOver(null)}
                                    onDrop={dragFile && f.name !== dragFile.folder ? (e) => { e.preventDefault(); doMoveToFolder(f.name) } : undefined}>
                                    <span title={docCategoryLabel(f.name)}>{docCategoryLabel(f.name)}</span>
                                    <span className="muted miller-count">{f.files.length}</span>
                                    <span className="miller-chev">›</span>
                                </button>
                            ))}
                            {selMonth != null && (rootFiles.length > 0 || isRoot) && (
                                <button type="button" className={`miller-item${isRoot ? ' active' : ''}`}
                                    onClick={() => pickCategory(MONTH_ROOT_FOLDER)}>
                                    <span title={docCategoryLabel(MONTH_ROOT_FOLDER)}>{docCategoryLabel(MONTH_ROOT_FOLDER)}</span>
                                    <span className="muted miller-count">{rootFiles.length}</span>
                                    <span className="miller-chev">›</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Column 4: Files (drop zone) */}
                    <div className={`miller-col files${dragCat === selCategory && selCategory ? ' dragover' : ''}`}
                        onDragOver={(e) => { if (canWrite && selCategory && !isRoot) { e.preventDefault(); setDragCat(selCategory) } }}
                        onDragLeave={() => setDragCat(null)}
                        onDrop={canWrite && selCategory && !isRoot ? onDrop(selCategory) : undefined}>
                        <div className="miller-head">
                            {T.files}
                            {selCategory && canWrite && !isRoot && (
                                <label className="link-btn">
                                    <input type="file" multiple style={{ display: 'none' }}
                                        onChange={(e) => { if (e.target.files) requestUpload(selCategory, e.target.files); e.target.value = '' }} />
                                    {T.addFiles}
                                </label>
                            )}
                        </div>
                        <div className="miller-list">
                            {!selCategory ? <p className="muted miller-empty">{T.pickType}</p>
                                : files.length === 0 ? <p className="muted miller-empty">{canWrite && !isRoot ? T.dropHere : T.empty}</p>
                                    : files.map((file) => (
                                        <div key={file.name} className="miller-file"
                                            draggable={canWrite && !isRoot}
                                            onDragStart={canWrite && !isRoot ? (e) => { setDragFile({ folder: selCategory, name: file.name }); e.dataTransfer.effectAllowed = 'move' } : undefined}
                                            onDragEnd={() => { setDragFile(null); setDragOver(null) }}>
                                            <button type="button" className="miller-file-name as-link" title={file.displayName ?? file.name}
                                                onClick={() => openFile(selCategory, file.name, file.displayName, file.invoiceId)}>
                                                {file.displayName ?? file.name}
                                            </button>
                                            {selCategory === 'vendor-invoices' && file.expenseType && (
                                                <span className="badge" title={T.expenseTypeTitle}>{typeLabel(file.expenseType)}</span>
                                            )}
                                            {selCategory === 'vendor-invoices' && (
                                                file.docId ? (
                                                    <input type="text" inputMode="decimal" className="miller-amount"
                                                        style={{ textAlign: 'right' }}
                                                        title={T.amountTitle} placeholder={fill(T.amountPlaceholder, { sign: moneySign() })}
                                                        defaultValue={file.amount != null ? file.amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                                        disabled={!canWrite}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                                        onBlur={(e) => saveAmount(selCategory, file.name, e.target.value)} />
                                                ) : <span className="muted miller-amount-na" title={T.untrackedTitle}>–</span>
                                            )}
                                            <span className="muted miller-file-size">{fileSize(file.size)}</span>
                                            {(canDelete || (selCategory === 'vendor-invoices' && file.docId && canWrite)) && (
                                                <button className="icon-action" title={T.actions} aria-label={T.actions}
                                                    aria-haspopup="menu" aria-expanded={fileMenu?.name === file.name}
                                                    onClick={(e) => {
                                                        if (fileMenu?.name === file.name) { setFileMenu(null); return }
                                                        const r = e.currentTarget.getBoundingClientRect()
                                                        setFileMenu({ name: file.name, x: r.right, y: r.bottom + 4 })
                                                    }}>⋮</button>
                                            )}
                                        </div>
                                    ))}
                        </div>
                    </div>
                </div>
            </div>
              </div>

              {/* Checkliste (Mitte) */}
              <div className="export-mid">
                {selMonth != null && (
                    <div className="panel">
                        <h2>
                            {fill(T.checklistTitle, { month: String(selMonth).padStart(2, '0'), year: selYear })}
                            {checklist && <span className="muted">{fill(T.doneCount, { done: checklist.doneCount, total: checklist.items.length })}</span>}
                            {checklist?.frozen && <span className="muted" title={T.frozenTitle}>{T.frozen}</span>}
                        </h2>
                        {checklist && (
                            <ul className="check-list">
                                {checklist.items.map((it) => (
                                    <li key={it.id} className="check-item">
                                        {editId === it.id ? (
                                            <div className="row check-edit">
                                                <input type="text" value={editLabel} autoFocus required
                                                    onChange={(e) => setEditLabel(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(it.id, e); if (e.key === 'Escape') cancelEdit() }} />
                                                <select value={editBinding} onChange={(e) => setEditBinding(e.target.value)}>
                                                    <option value="">{T.noBinding}</option>
                                                    {bindings.map((b) => {
                                                        const used = bindingUsed(b.key, it.id)
                                                        return <option key={b.key} value={b.key} disabled={used}>{b.label}{used ? T.bindingUsed : ''}</option>
                                                    })}
                                                </select>
                                                <select value={editRequirement === 'may-be-empty' && !editCanBeEmpty ? 'required' : editRequirement}
                                                    title={T.appliesToAllMonths}
                                                    onChange={(e) => setEditRequirement(e.target.value as ChecklistRequirement)}>
                                                    <option value="required">{T.required}</option>
                                                    <option value="optional">{T.optional}</option>
                                                    <option value="may-be-empty" disabled={!editCanBeEmpty}>{T.mayBeEmpty}</option>
                                                </select>
                                                {editCanBeEmpty && (
                                                    <label className="row" title={T.appliesToAllMonths}>
                                                        <input type="checkbox" checked={editMailNote} onChange={(e) => setEditMailNote(e.target.checked)} />
                                                        {T.mailNote}
                                                    </label>
                                                )}
                                                <button className="secondary" disabled={anyBusy} onClick={(e) => saveEdit(it.id, e)}>
                                                    {isBusy('edititem') ? '…' : T.save}
                                                </button>
                                                <button type="button" className="secondary" disabled={anyBusy} onClick={cancelEdit}>{T.cancel}</button>
                                            </div>
                                        ) : (
                                            <>
                                                <label className="check-main">
                                                    <input type="checkbox" checked={it.done}
                                                        disabled={!canWrite || checklist.frozen || it.autoSatisfied || !!it.noMovement}
                                                        onChange={() => toggleItem(it.id, it.manualDone)} />
                                                    <span className={`check-label${it.done ? ' check-done' : ''}`}>{it.label}</span>
                                                </label>
                                                <span className="check-col">
                                                    {it.binding && (it.noMovement && !it.autoSatisfied
                                                        ? <Mark mark="noMovement" detail={it.noMovement.brands.length ? it.noMovement.brands.join(', ') : it.binding} />
                                                        : <Mark mark={it.autoSatisfied ? 'autoDone' : 'auto'} detail={it.binding} />)}
                                                </span>
                                                <span className="check-col">
                                                    {it.requirement === 'optional' && <Mark mark="optional" />}
                                                    {it.requirement === 'may-be-empty' && <Mark mark="mayBeEmpty" />}
                                                    {it.requirement === 'may-be-empty' && it.mailNote && <Mark mark="mailNote" />}
                                                </span>
                                                <span className="check-col check-actions">
                                                    {canWrite && !checklist.frozen && (
                                                        <>
                                                            <button className="icon-action" title={T.editItemTitle} aria-label={T.edit}
                                                                onClick={() => startEdit(it)}>✎</button>
                                                            <button className="icon-action" title={T.removeItemTitle} aria-label={T.remove}
                                                                onClick={() => removeItem(it.id)}>×</button>
                                                        </>
                                                    )}
                                                </span>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {checklist && checklist.items.length > 0 && (
                            <div className="check-legend muted">
                                {(Object.keys(CHECK_MARK_SYMBOLS) as CheckMark[]).map((m) => (
                                    <span key={m}><span className={`check-mark ${CHECK_MARK_SYMBOLS[m].cls}`} aria-hidden="true">{CHECK_MARK_SYMBOLS[m].sym}</span> {CHECK_MARKS[m]}</span>
                                ))}
                            </div>
                        )}
                        {canWrite && !checklist?.frozen && (
                            <div className="row check-add">
                                <input type="text" placeholder={T.newItemPlaceholder} value={newLabel} required
                                    onChange={(e) => setNewLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') addItem(e) }} />
                                <select value={newBinding} onChange={(e) => setNewBinding(e.target.value)}>
                                    <option value="">{T.noBinding}</option>
                                    {bindings.map((b) => {
                                        const used = bindingUsed(b.key)
                                        return <option key={b.key} value={b.key} disabled={used}>{b.label}{used ? T.bindingUsed : ''}</option>
                                    })}
                                </select>
                                <button className="secondary" disabled={anyBusy} onClick={(e) => addItem(e)}>
                                    {T.add}
                                </button>
                            </div>
                        )}
                    </div>
                )}
              </div>

              {/* Actions (rechts, Buttons untereinander) */}
              <div className="export-side">
                {selMonth != null && (
                    <div className="panel">
                        <h2>
                            {T.actions}
                            {openItems.length > 0 && (
                                <InfoTip>
                                    {T.sendLocked}<br />
                                    {fill(T.openListSentence, { items: openItems.join(', ') })}
                                </InfoTip>
                            )}
                        </h2>
                        <div className="actions-col">
                            <button className="secondary" disabled={isBusy('report')} onClick={loadReport}>
                                {isBusy('report') ? '…' : T.showReport}
                            </button>
                            <button type="button" className="secondary" onClick={() => void openReport()}>{T.reportPdf}</button>
                            {canWrite && (
                                <button className="secondary" disabled={isBusy('stripe') || zipRunning} onClick={doStripe}
                                    title={T.stripeTitle}>
                                    {isBusy('stripe') ? '…' : T.stripe}
                                </button>
                            )}
                            {canWrite && (
                                <button className="secondary" disabled={isBusy('paypal') || zipRunning} onClick={doPaypal}>
                                    {isBusy('paypal') ? '…' : T.paypal}
                                </button>
                            )}
                            {canWrite && (
                                <button className="secondary" disabled={isBusy('mollie') || zipRunning} onClick={doMollie}
                                    title={T.mollieTitle}>
                                    {isBusy('mollie') ? '…' : T.mollie}
                                </button>
                            )}
                            {canWrite && (
                                <button disabled={sendDisabled} onClick={doSend}
                                    title={openItems.length ? fill(T.openList, { items: openItems.join(', ') }) : T.send}>
                                    {isBusy('send') ? '…' : T.send}
                                </button>
                            )}
                            {canWrite && (
                                <button className="secondary" disabled={anyBusy} onClick={() => toggleClosed(!monthClosed)}
                                    title={monthClosed
                                        ? T.reopenTitle
                                        : T.closeTitle}>
                                    {isBusy('closed') ? '…' : (monthClosed ? T.reopen : T.close)}
                                </button>
                            )}
                        </div>
                        {statusText && (
                            <p className="hint" style={{ marginTop: 10 }} title={checklist?.sentTo ? fill(T.recipient, { to: checklist.sentTo }) : undefined}>
                                {statusText}
                            </p>
                        )}
                    </div>
                )}
              </div>
            </div>

            {/* Report-Vorschau (volle Breite unter dem Explorer) */}
            {selMonth != null && report && (
                <div className="panel">
                    <h2>{fill(T.reportTitle, { month: String(selMonth).padStart(2, '0'), year: selYear })}</h2>
                    <div className="cards">
                        <div className="card"><div className="value">{money(report.totals.net, report.currency)}</div><div className="label">{T.net}</div></div>
                        <div className="card"><div className="value">{money(report.totals.tax, report.currency)}</div><div className="label">{T.tax}</div></div>
                        <div className="card"><div className="value">{money(report.totals.gross, report.currency)}</div><div className="label">{T.gross}</div></div>
                    </div>
                    {report.perCountry.length > 0 && (
                        <table>
                            <thead>
                                <tr><th>{T.colCountry}</th><th className="num">{T.colCount}</th><th className="num">{T.net}</th><th className="num">{T.tax}</th><th className="num">{T.gross}</th></tr>
                            </thead>
                            <tbody>
                                {report.perCountry.map((c) => (
                                    <tr key={c.country}>
                                        <td>{c.country || '—'}</td>
                                        <td className="num">{c.count}</td>
                                        <td className="num">{money(c.net, report.currency)}</td>
                                        <td className="num">{money(c.tax, report.currency)}</td>
                                        <td className="num">{money(c.gross, report.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* OSS: die Zahlen für die One-Stop-Shop-Meldung. Bewusst eigene Tabelle —
                        es ist eine TEILMENGE der Ländertabelle oben (nur EU-grenzüberschreitend
                        B2C), die beiden dürfen nicht addiert werden. */}
                    <h3 style={{ marginTop: 24, marginBottom: 4 }}>
                        {T.ossTitle}
                        <InfoTip>{fill(T.ossTip, { baseCountry: report.ossBaseCountry || '—' })}</InfoTip>
                    </h3>
                    {report.oss.length === 0 ? (
                        <p className="muted" style={{ marginTop: 0 }}>{T.ossNone}</p>
                    ) : (
                        <table>
                            <thead>
                                <tr><th>{T.colCountry}</th><th className="num">{T.colRate}</th><th className="num">{T.colInvoices}</th><th className="num">{T.net}</th><th className="num">{T.tax}</th></tr>
                            </thead>
                            <tbody>
                                {report.oss.map((o) => (
                                    <tr key={`${o.country}-${o.taxRate}`}>
                                        <td>{o.country}</td>
                                        <td className="num">{o.taxRate} %</td>
                                        <td className="num">{o.count}</td>
                                        <td className="num">{money(o.net, report.currency)}</td>
                                        <td className="num">{money(o.tax, report.currency)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan={3}><strong>{T.ossSum}</strong></td>
                                    <td className="num"><strong>{money(report.ossTotals.net, report.currency)}</strong></td>
                                    <td className="num"><strong>{money(report.ossTotals.tax, report.currency)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            </>)}

            {/* Beleg-Eingang (E-Mail-Ingestion): status + problem list */}
            {view === 'inbox' && (
            <div className="panel">
                <h2>
                    {T.inboxTitle}
                    <InfoTip>{T.inboxTip}</InfoTip>
                </h2>
                {ingest.length === 0 ? (
                    <p className="muted">{T.inboxEmpty}</p>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>{T.colStatus}</th><th>{T.colReceived}</th><th>{T.colFrom}</th><th>{T.colSubject}</th>
                                <th>{T.colInvoice}</th><th>{T.colFiles}</th><th>{T.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ingest.map((it) => {
                                const st = INGEST_STATUS[it.status] ?? { label: it.status, cls: '' }
                                const busyId = (a: string) => ingestBusy === `${it._id}:${a}`
                                const anyBusy = ingestBusy?.startsWith(`${it._id}:`)
                                return (
                                    <tr key={it._id}>
                                        <td><span className={`badge ${st.cls}`} title={it.reason ? INGEST_REASON[it.reason] ?? it.reason : ''}>{st.label}</span></td>
                                        <td>{shortDate(it.createdAt)}</td>
                                        <td title={it.from}>{(it.from ?? '—').replace(/.*<(.+)>.*/, '$1').slice(0, INGEST_FROM_MAX_CHARS)}</td>
                                        <td title={it.subject}>{(it.subject ?? '—').slice(0, INGEST_SUBJECT_MAX_CHARS)}</td>
                                        <td>
                                            {it.status === 'imported'
                                                ? <>{shortDate(it.issueDate)}{it.amount != null && <> · {money(it.amount)}</>}</>
                                                : <span className="muted">{it.reason ? INGEST_REASON[it.reason] ?? it.reason : '—'}</span>}
                                        </td>
                                        <td>
                                            {it.savedFiles?.length ? <span title={it.savedFiles.join('\n')}>{fill(T.savedCount, { n: it.savedFiles.length })}</span> : null}
                                            {it.skippedFiles?.length ? <span className="muted" title={it.skippedFiles.join('\n')}>{it.savedFiles?.length ? ', ' : ''}{fill(T.skippedCount, { n: it.skippedFiles.length })}</span> : null}
                                            {it.duplicateFiles?.length ? <span className="muted" title={it.duplicateFiles.join('\n')}>{(it.savedFiles?.length || it.skippedFiles?.length) ? ', ' : ''}{fill(T.duplicateCount, { n: it.duplicateFiles.length })}</span> : null}
                                            {!it.savedFiles?.length && !it.skippedFiles?.length && !it.duplicateFiles?.length ? '—' : null}
                                        </td>
                                        <td>
                                            {it.status === 'problem' && canWrite ? (
                                                <div className="row" style={{ gap: 6 }}>
                                                    <button className="link-btn" disabled={!!anyBusy} onClick={() => doIngestAction(it._id, 'retry')}>
                                                        {busyId('retry') ? '…' : T.retry}
                                                    </button>
                                                    <button className="link-btn" disabled={!!anyBusy} onClick={() => doIngestAction(it._id, 'accept')}
                                                        title={T.acceptTitle}>
                                                        {busyId('accept') ? '…' : T.accept}
                                                    </button>
                                                    <button className="link-btn" disabled={!!anyBusy} onClick={() => doIngestAction(it._id, 'ignore')}>
                                                        {busyId('ignore') ? '…' : T.ignore}
                                                    </button>
                                                </div>
                                            ) : <span className="muted">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            )}
            </div>

            <ConfirmDialog
                open={!!toDelete}
                title={T.deleteFileTitle}
                message={toDelete ? <>{fill(T.deleteFileQuestion, { name: shownName(toDelete.folder, toDelete.name) })}</> : null}
                confirmLabel={T.delete}
                danger
                busy={delBusy}
                onConfirm={confirmDelete}
                onCancel={() => { if (!delBusy) setToDelete(null) }}
            />

            <ConfirmDialog
                open={!!toDeleteDir}
                title={toDeleteDir?.kind === 'year' ? T.deleteYearTitle : T.deleteMonthTitle}
                message={toDeleteDir ? <>{fill(T.deleteDirQuestion, { label: toDeleteDir.kind === 'year' ? fill(T.yearLabel, { year: toDeleteDir.value }) : `${monthName(toDeleteDir.value)} ${selYear}` })}</> : null}
                confirmLabel={T.delete}
                danger
                busy={dirDelBusy}
                error={error}
                onConfirm={confirmDeleteDir}
                onCancel={() => { if (!dirDelBusy) { setToDeleteDir(null); setError('') } }}
            />

            {pendingUpload && (
                <PeriodDialog
                    title={T.uploadTitle}
                    confirmLabel={T.upload}
                    typeLabel={docCategoryLabel(pendingUpload.folder)}
                    count={pendingUpload.files.length}
                    initYear={selYear}
                    initMonth={selMonth ?? new Date().getMonth() + 1}
                    years={years}
                    busy={isBusy('upload')}
                    error={error}
                    onConfirm={doUpload}
                    onCancel={() => { if (!isBusy('upload')) { setPendingUpload(null); setError('') } }}
                />
            )}

            {pendingMove && (
                <PeriodDialog
                    title={fill(T.moveTitle, { name: pendingMove.name })}
                    confirmLabel={T.move}
                    typeLabel={docCategoryLabel(pendingMove.folder)}
                    initYear={pendingMove.toYear}
                    initMonth={selMonth ?? new Date().getMonth() + 1}
                    years={years}
                    busy={isBusy('move')}
                    error={error}
                    onConfirm={doMove}
                    onCancel={() => { if (!isBusy('move')) { setPendingMove(null); setError('') } }}
                />
            )}

            {/* Per-file ⋮ menu — fixed-positioned so the scrolling list can't clip it. */}
            {fileMenu && selCategory && (() => {
                const file = files.find((f) => f.name === fileMenu.name)
                if (!file) return null
                const canAssign = selCategory === 'vendor-invoices' && !!file.docId && canWrite
                return (
                    <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onMouseDown={() => setFileMenu(null)} />
                        <div className="menu-dropdown" role="menu"
                            style={{ position: 'fixed', top: fileMenu.y, left: fileMenu.x, right: 'auto', transform: 'translateX(-100%)' }}>
                            {canAssign && (
                                <button role="menuitem" onClick={() => { openExpenseDialog(file.name, file.expenseType); setFileMenu(null) }}>
                                    {T.assignExpenseTypeMenu}
                                </button>
                            )}
                            {canDelete && (
                                <button role="menuitem" onClick={() => { setToDelete({ folder: selCategory, name: file.name }); setFileMenu(null) }}>
                                    {T.delete}
                                </button>
                            )}
                        </div>
                    </>
                )
            })()}

            {/* Rechtsklick-Kontextmenü auf Jahr/Monat — nur leere Ordner sind löschbar. */}
            {dirMenu && (() => {
                const isYear = dirMenu.kind === 'year'
                const label = isYear ? fill(T.yearLabel, { year: dirMenu.value }) : `${monthName(dirMenu.value)} ${selYear}`
                const empty = isYear ? !(index[String(dirMenu.value)]?.length) : monthEmptyKnown(dirMenu.value)
                const blocked = empty === false // sicher nicht leer → nicht löschbar
                const hint = isYear ? T.hintHasMonths : T.hintNotEmpty
                return (
                    <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onMouseDown={() => setDirMenu(null)} />
                        <div className="menu-dropdown" role="menu"
                            style={{ position: 'fixed', top: dirMenu.y, left: dirMenu.x, right: 'auto' }}>
                            <button role="menuitem" disabled={blocked}
                                onClick={() => { setError(''); setToDeleteDir({ kind: dirMenu.kind, value: dirMenu.value }); setDirMenu(null) }}>
                                {blocked ? fill(T.deleteLabelBlocked, { label, hint }) : fill(T.deleteLabel, { label })}
                            </button>
                        </div>
                    </>
                )
            })()}

            <Drawer
                open={!!expenseFor}
                title={T.assignExpenseType}
                width={420}
                onClose={() => { if (!isBusy('expense')) { setExpenseFor(null); setError('') } }}
                footer={
                    <>
                        <button type="button" onClick={saveExpenseType} disabled={isBusy('expense')}>{isBusy('expense') ? '…' : T.save}</button>
                        <button type="button" className="secondary" onClick={() => { setExpenseFor(null); setError('') }} disabled={isBusy('expense')}>{T.cancel}</button>
                    </>
                }>
                <p className="muted">{expenseFor?.name}</p>
                <div className="field">
                    <label>{T.type}</label>
                    <select value={expenseSel} disabled={isBusy('expense')} onChange={(e) => setExpenseSel(e.target.value)}>
                        <option value="">{T.noType}</option>
                        {expenseTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                </div>
                {error && <div className="notice err">{error}</div>}
            </Drawer>
        </div>
    )
}
